import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocalStorage } from "@rehooks/local-storage";
import debounce from "lodash-es/debounce";

import {
  DS_COMPONENT_PREFIX,
  EXCLUDE_KEY,
  FILTER_KEY,
  HIGHLIGHT_CLASS,
  HIGHLIGHTS_ENABLED,
} from "./constants";
import { findFirstDOMNode, isDevToolsElement } from "./helpers";
import { SHOW_DEVTOOLS_KEY } from "../constants";

const style = document.createElement("style");
style.id = "devtools-highlight-style";
style.innerHTML = `
  .${HIGHLIGHT_CLASS} {
    outline: 2px dashed #ff4081 !important;
  }
`;

const ORIGINAL_TITLE_ATTR = "data-original-title";

const clearHighlights = () => {
  document.querySelectorAll(`.${HIGHLIGHT_CLASS}`).forEach((element) => {
    element.classList.remove(HIGHLIGHT_CLASS);

    // Restore original title if it existed
    const originalTitle = element.getAttribute(ORIGINAL_TITLE_ATTR);
    if (originalTitle) {
      element.setAttribute("title", originalTitle);
    } else {
      element.removeAttribute("title");
    }
    element.removeAttribute(ORIGINAL_TITLE_ATTR);
  });
};

export const useXRay = () => {
  const [isEnabled] = useLocalStorage(HIGHLIGHTS_ENABLED, false);
  const [filters] = useLocalStorage<string[]>(FILTER_KEY, []);
  const [isExcludeMode] = useLocalStorage(EXCLUDE_KEY, false);
  const [showDevTools] = useLocalStorage(SHOW_DEVTOOLS_KEY, false);
  const observerRef = useRef<MutationObserver | null>(null);

  const highlightComponents = useCallback(() => {
    if (!showDevTools || !isEnabled) {
      clearHighlights();
      return;
    }

    // First, clear all highlights
    clearHighlights();

    // Keep track of elements and their DS components
    const elementComponents = new Map<Element, Set<string>>();

    // Find all React elements
    document.querySelectorAll("[class]").forEach((element) => {
      if (isDevToolsElement(element)) return;

      const fiberKey = Object.keys(element).find((key) => key.startsWith("__reactFiber$"));
      if (!fiberKey) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let fiber = (element as any)[fiberKey];

      while (fiber) {
        const displayName = fiber.type?.displayName;
        if (!displayName?.startsWith(DS_COMPONENT_PREFIX)) {
          fiber = fiber.return;
          continue;
        }

        let shouldHighlight = filters?.length > 0 ? filters.includes(displayName) : true;

        if (filters?.length > 0 && isExcludeMode) {
          shouldHighlight = !shouldHighlight;
        }

        if (!shouldHighlight) {
          fiber = fiber.return;
          continue;
        }

        const domNode = findFirstDOMNode(fiber);
        if (!domNode) {
          fiber = fiber.return;
          continue;
        }

        if (!elementComponents.has(domNode)) {
          elementComponents.set(domNode, new Set());
        }
        elementComponents.get(domNode)!.add(displayName);
        fiber = fiber.return;
      }
    });

    // Apply highlights and titles in a single pass
    elementComponents.forEach((displayNames, element) => {
      element.classList.add(HIGHLIGHT_CLASS);

      // Store original title if it exists
      const originalTitle = element.getAttribute("title");
      if (originalTitle) {
        element.setAttribute(ORIGINAL_TITLE_ATTR, originalTitle);
      }

      // Set our new title
      const title = Array.from(displayNames).reverse().join(" → ");
      element.setAttribute("title", title);
    });
  }, [showDevTools, isEnabled, filters, isExcludeMode]);

  const debouncedHighlight = useMemo(
    () => debounce(highlightComponents, 100),
    [highlightComponents]
  );

  useEffect(() => {
    if (!document.getElementById(style.id)) {
      document.head.appendChild(style);
    }

    debouncedHighlight();

    if (isEnabled && showDevTools) {
      observerRef.current = new MutationObserver(debouncedHighlight);
      observerRef.current.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      debouncedHighlight.cancel(); // Cancel any pending debounced calls
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [debouncedHighlight, isEnabled, showDevTools]);

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
      clearHighlights();
    };
  }, []);
};
